// Native breaks arrive in milliseconds; POS breaks arrive in seconds.
export function timestampMillis(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = typeof value === "number" ? value : Date.parse(value);
  if (!Number.isFinite(number)) return null;
  return typeof value === "number" && Math.abs(number) < 1e11 ? number * 1000 : number;
}

// Same format as the clock in/out column. timeZone is optional: the timecard
// table uses the viewer's browser time like its clock column; the custom range
// report passes the store's timezone so both its clock and break times use it.
export function formatBreakTime(value, timeZone) {
  const milliseconds = timestampMillis(value);
  if (milliseconds === null) return "";
  return new Date(milliseconds).toLocaleTimeString([], {
    hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone } : {}),
  });
}

// Times are shown to the minute, so "inside the shift" and "overlapping" are
// judged to the minute too: a break punched at 10:41:10 fits a 10:41 clock out.
function toMinute(ms) {
  return Number.isFinite(ms) ? Math.floor(ms / 60000) * 60000 : ms;
}

export function breakBoundsError(breaks, start, end) {
  if (!Number.isFinite(start)) return null;
  const shiftStart = toMinute(start);
  const shiftEnd = end != null ? toMinute(end) : null;
  const outside = (breaks || []).some(b =>
    (b.clocked_in_at != null && toMinute(b.clocked_in_at) < shiftStart) ||
    (shiftEnd != null && b.clocked_in_at != null && toMinute(b.clocked_in_at) > shiftEnd) ||
    (shiftEnd != null && b.clocked_out_at != null && toMinute(b.clocked_out_at) > shiftEnd)
  );
  return outside ? "This shift would exclude a recorded break. Adjust the shift or break times so every break falls inside the shift." : null;
}

// Row-level errors for breaks a manager entered or edited. Breaks are never
// moved or clamped; the manager fixes the times themselves.
export function validateBreaks(breaks, start, end) {
  const errors = {};
  const rows = (breaks || []).map((brk, index) => ({ brk, index }));
  for (const { brk, index } of rows) {
    const breakStart = brk.clocked_in_at;
    const breakEnd = brk.clocked_out_at;
    if (!Number.isFinite(breakStart)) {
      errors[index] = "Enter a break start time";
    } else if (end != null && !Number.isFinite(breakEnd)) {
      errors[index] = "Enter a break end time";
    } else if (Number.isFinite(breakEnd) && breakEnd <= breakStart) {
      errors[index] = "Break end must be after break start";
    } else if (
      (Number.isFinite(start) && toMinute(breakStart) < toMinute(start)) ||
      (end != null && Number.isFinite(end) && toMinute(breakEnd) > toMinute(end))
    ) {
      errors[index] = "Break must be within the shift";
    }
  }
  const timed = rows
    .filter(({ index, brk }) => !errors[index] && Number.isFinite(brk.clocked_out_at))
    .sort((a, b) => a.brk.clocked_in_at - b.brk.clocked_in_at);
  for (let i = 1; i < timed.length; i++) {
    if (toMinute(timed[i].brk.clocked_in_at) < toMinute(timed[i - 1].brk.clocked_out_at)) {
      errors[timed[i].index] = "Breaks can't overlap";
    }
  }
  return errors;
}

// Rounds down like the server's break_minutes so the drawer matches the table.
export function breakTotals(breaks) {
  const minutes = (paid) => Math.floor((breaks || []).reduce((sum, brk) => (
    Boolean(brk.is_paid) === paid && Number.isFinite(brk.clocked_in_at) && Number.isFinite(brk.clocked_out_at)
      ? sum + Math.max(0, brk.clocked_out_at - brk.clocked_in_at)
      : sum
  ), 0) / 60000);
  return { unpaid: minutes(false), paid: minutes(true) };
}
